/**
 * Renders grouped reasoning + tool-call parts as an expandable section.
 *
 * While running: shows a "Thinking" shimmer header with the latest
 * reasoning text streaming below it, and tool cards as they're invoked.
 * After completion: collapses into a "N tool calls" accordion trigger.
 *
 * Uses assistant-ui's ChainOfThought primitives for part grouping,
 * collapse state, and per-part rendering context.
 */
import { useEffect, useMemo, type FC } from "react";
import { ChainOfThoughtPrimitive, useAui, useAuiState } from "@assistant-ui/react";
import { ChevronRight } from "lucide-react";
import { DefaultToolMessage } from "~/components/assistant-v2/DefaultToolMessage";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import { cn } from "~/lib/utils";

/**
 * Reasoning parts render as nothing inside the parts list — the latest
 * reasoning text is shown in the trigger instead.
 */
const CoTReasoningPart: FC = () => null;

export const ChainOfThought: FC = () => {
  const aui = useAui();
  const { parts, collapsed, status } = useAuiState((s) => s.chainOfThought);
  // ChainOfThoughtClient derives status from the last part's status field.
  // Reasoning parts have no status, so a reasoning-only group falls through
  // to "complete". Fall back to the message-level status so we correctly
  // detect the running state before the first tool call arrives.
  const messageIsRunning = useAuiState((s) => s.message.status?.type === "running");
  const isRunning = status.type === "running" || messageIsRunning;

  useEffect(() => {
    aui.chainOfThought().setCollapsed(!isRunning);
  }, [isRunning, aui]);

  const toolCount = parts.filter((p) => p.type === "tool-call").length;

  const latestReasoning = useMemo(() => {
    const reasoningParts = parts.filter((p) => p.type === "reasoning");

    const last = reasoningParts[reasoningParts.length - 1];
    if (!last || last.type !== "reasoning") return null;
    // The backend injects U+001E between reasoning segments in the aggregator buffer.
    return last.text.split("\u001e").pop()?.trim() || null;
  }, [parts]);

  if (parts.length === 0) return null;

  // Hide reasoning-only groups after completion — nothing useful to show.
  if (!isRunning && toolCount === 0) return null;

  const handleToggle = () => {
    aui.chainOfThought().setCollapsed(!collapsed);
  };

  return (
    <ChainOfThoughtPrimitive.Root>
      {/* Trigger row — always visible */}
      <div
        role="button"
        tabIndex={0}
        className="flex items-center cursor-pointer select-none w-fit gap-1"
        onClick={handleToggle}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleToggle()}
      >
        {isRunning ? (
          <ShimmerText className="text-sm font-medium">Thinking</ShimmerText>
        ) : (
          <span className="text-sm font-medium text-purple-600 pb-1">
            {toolCount} tool call{toolCount !== 1 ? "s" : ""}
          </span>
        )}
        <ChevronRight
          className={cn(
            "size-3.5 transition-transform duration-200",
            isRunning ? "text-gray-400" : "text-purple-600",
            !collapsed && "rotate-90"
          )}
        />
      </div>

      {/* Expanded content — reasoning text + tool cards */}
      {!collapsed && (
        <div className="mt-1.5">
          {isRunning && latestReasoning && (
            <p className="text-sm leading-relaxed text-gray-500 mb-3">{latestReasoning}</p>
          )}
          <div className="space-y-2">
            <ChainOfThoughtPrimitive.Parts
              components={{
                Reasoning: CoTReasoningPart,
                tools: { Fallback: DefaultToolMessage },
              }}
            />
          </div>
        </div>
      )}
    </ChainOfThoughtPrimitive.Root>
  );
};
